import React, { useState } from "react";
import { Button, DialogActions, DialogContent, DialogTitle, Divider, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import TabbedDialog from "components/dialogs/TabbedDialog";
import SettingsOptions from "components/settingsConfig/SettingsOptions";
import SplitSettingsWrapper from "components/settingsConfig/SplitSettingsWrapper";
import SettingsAccordion from "components/settingsConfig/uiComponents/SettingsAccordion";
import SettingsToggleButton from "components/settingsConfig/uiComponents/SettingsToggleButton";
import ColumnManager, { densityOptions } from "components/tables/columns/ColumnManager";
import { ColumnModel, ColumnModelItem, ColumnToolbarType } from "components/tables/columns/ColumnModel";
import ResponsiveDialog from "components/dialogs/ResponsiveDialog";
import { topicTableDefaultSettings, TopicTableSettingsStateType } from "./topicTableUtils";
import { initialTopicColumns } from "./TopicTable";
import StaticTableDialogContent from "../../../tables/StaticTableDialogContent";
import TopicQuerySettings from "components/widgets/utility/TopicQuerySettings";
import { ClusteringBias, normalizeClusteringBias } from "components/widgets/utility/ClusteringBias";
import { AutoWidgetQueryBehavior, getSingleOriginalRequestByName, getSingleWidgetResponseByName, WidgetRequest } from "system/widget/Widget";
import { WidgetSettingsProps } from "components/widgetParts/WidgetSettingsWrapper";
import SettingsWidgetTitleSelect from "components/settingsConfig/uiComponents/SettingsWidgetTitleSingleSelect";
import SettingsTextField from "components/settingsConfig/uiComponents/SettingsTextField";
import SettingsCheckbox from "components/settingsConfig/uiComponents/SettingsCheckbox";

const defaultWidgetRequest = {
  name: 'topic-table',
  endpoint: 'query/topics',
  queryParams: { },
  autoWidgetQueryBehavior: AutoWidgetQueryBehavior.AUTO_SINGLE
} as WidgetRequest;

export function BuildDefaultTopicTableRequests(): WidgetRequest[] | undefined  {
  const widgetRequest : WidgetRequest = { ...defaultWidgetRequest };
  const requestInitBody = {
    topics: {
      size: 100,
      types: ["topic"]
    }
  };
  widgetRequest.requestInit = { body: requestInitBody };
  return [widgetRequest];
}

export default function TopicTableSettings(props: WidgetSettingsProps): React.JSX.Element {
  const { i18n } = useTranslation();

  const apiResponseInfo = getSingleWidgetResponseByName(props.apiResponses, 'topic-table');
  const originalRequest = getSingleOriginalRequestByName(props.activeWidgetRequests, 'topic-table');
  const responseBody = apiResponseInfo?.responseBody;
  const requestBody = originalRequest?.requestInit?.body;

  const fieldLabels: Record<string, string> = responseBody?.field_labels ?? [];

  const apiData = responseBody?.topics ? responseBody?.topics.map((topic: any) => topic.label).sort() : [];
  const [excludedTerms, setExcludedTerms] = useState<string[]>(props.widget?.data?.excludedTerms || []);
  const [bulkExclusionDialogIsOpen, setBulkExclusionDialogIsOpen] = useState<boolean>(false);
  const [selectedFieldIds, setSelectedFieldIds] = useState<string[]>(requestBody?.topics?.fields || []);
  const [selectedTopicsTypes, setSelectedTopicsTypes] = useState<string[]>(requestBody?.topics?.types || []);
  const [queryTermExclusion, setQueryTermExclusion] = useState<string>(requestBody?.topics?.query_term_exclusion || "all");
  const [clusteringBias, setClusteringBias] = useState<ClusteringBias>(normalizeClusteringBias(props.widget?.data?.clusteringBias));
  const [sortBy, setSortBy] = useState<string>(requestBody?.topics?.sort || props.widget?.data?.sortBy || "relevance");

  const initialColumnModel = {
    columns: initialTopicColumns.map((colItem: ColumnModelItem) => {
      let headerName = fieldLabels[colItem.field] ?? colItem.field;

      if (colItem.field === "cluster_id") {
        headerName = i18n.t("cluster");
      }

      return {
        id: colItem.field,
        key: colItem.sourceField,
        field: colItem.field,
        headerName,
        fieldType: colItem.fieldType,
        sourceField: colItem.sourceField,
        renderType: colItem.renderType ?? "text"
      };
    }),
  };

  const [columnModelState, setColumnModelState] = useState<ColumnModel>(
    {
      ...initialColumnModel,
      ...props.widget?.data?.columnModel
    }
  );

  const [tabValue, setTabValue] = useState<number>(0);
  const handleTabChange = (event: React.SyntheticEvent<Element, Event>, newValue: number) => setTabValue(newValue);

  const [settingsState, setSettingsState] = useState<TopicTableSettingsStateType>({...topicTableDefaultSettings, ...props?.widget?.data?.settings});
  const settingsOnChange = (updatedState: TopicTableSettingsStateType) => setSettingsState(updatedState);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const handleOpenDialog = () => setIsDialogOpen(true);
  const handleCloseDialog = () => setIsDialogOpen(false);
  const [columnsToAdd, setColumnsToAdd] = useState<ColumnModelItem[] | []>([]);

  function updateColumnModelState(newColumnModel: ColumnModel) {
    setColumnModelState(newColumnModel);
  }

  function handleColumnDialogMode() {
    handleOpenDialog();
  }

  function handleHideColumn(columnItem: ColumnModelItem) {
    const indexOfColumnToRemove = columnModelState.columns.findIndex((col: ColumnModelItem) => col.field === columnItem.field);
    const newModelColumnsList = columnModelState.columns.toSpliced(indexOfColumnToRemove, 1); // Makes new col list w/o removed column
    const updatedColumnToRemove = { ...columnItem, hidden: true };
    newModelColumnsList.push(updatedColumnToRemove);
    const newColumnModelState = { ...columnModelState, columns: newModelColumnsList };
    setColumnModelState(newColumnModelState);
  }

  function handleRemoveMultipleColumns(activeColumns: ColumnModelItem[]) {
    const removedColumns: ColumnModelItem[] = [];
    const updatedColumns = columnModelState.columns.filter((col: ColumnModelItem) => {
      if (activeColumns.includes(col)) {
        const colCopy = {...col};
        colCopy.hidden = true;
        removedColumns.push(colCopy);
      }
      return !activeColumns.includes(col);
    });

    const newColumnModelState = { ...columnModelState, columns: [...updatedColumns, ...removedColumns]};
    setColumnModelState(newColumnModelState);
  }

  function handlePrepColumnsToAdd(newColumns: ColumnModelItem[]) {
    setColumnsToAdd(newColumns);
  }

  function handleAddColumns(newColumns?: ColumnModelItem[]) {
    if (newColumns) {
      const filteredColumns = columnModelState.columns.filter((col: ColumnModelItem) => {
        return !newColumns?.includes(col);
      });

      const newColumnsToAdd = newColumns.map((col: ColumnModelItem) => ({ ...col, hidden: false }));
      updateColumnModelState({
        ...columnModelState,
        columns: [...filteredColumns, ...newColumnsToAdd]
      });
    }
  }

  function handleSetSelectedFieldIds(value: string[]) {
    setSelectedFieldIds(value);
  }

  function handleSetSelectedTopicsTypes(value: string[]) {
    setSelectedTopicsTypes(value);
  }

  function handleSetExcludedTerms(values: string[]) {
    setExcludedTerms(values);
  }

  function handleSetBulkExclusionDialogIsOpen(value: boolean) {
    setBulkExclusionDialogIsOpen(value);
  }

  function handleSetQueryTermExclusion(value: string) {
    setQueryTermExclusion(value);
  }

  const updateWidget = () => {
    const widgetRequest: WidgetRequest = { ...defaultWidgetRequest };
    const requestInitBody: {
      topics: {
        size: number | undefined,
        types?: string[],
        fields?: string[],
        exclude?: string[],
        query_term_exclusion?: string, // "required" | "none" | "only"
        clustering_bias?: number,
        sort?: string,
      }
    } = {
      topics: {
        size: settingsState.wordCount
      }
    };
    if (selectedTopicsTypes.length > 0) {
      requestInitBody.topics.types = selectedTopicsTypes;
    }
    if (selectedFieldIds.length > 0) {
      requestInitBody.topics.fields = selectedFieldIds;
    }
    if (excludedTerms.length > 0) {
      requestInitBody.topics.exclude = excludedTerms;
    }
    if (queryTermExclusion !== "all") {
      requestInitBody.topics.query_term_exclusion = queryTermExclusion;
    }
    requestInitBody.topics.clustering_bias = clusteringBias;
    if (sortBy !== "relevance") {
      requestInitBody.topics.sort = sortBy;
    }

    widgetRequest.requestInit = { body: requestInitBody };
    props.saveWidgetDataAndRequests({
      clusteringBias: clusteringBias,
      columnModel: columnModelState,
      excludedTerms: excludedTerms,
      sortBy: sortBy,
      settings: settingsState
    }, [widgetRequest]).then(() => {
      if (props.widgetRefresh) {
        props.widgetRefresh();
      }
    });
  }

  return (
    <TabbedDialog
      currentTab={tabValue}
      handleTabChange={handleTabChange}
      onCancel={() => props.setSettingsDialogIsOpen(false)}
      onConfirm={() => {
        updateWidget();
        props.setSettingsDialogIsOpen(false);
      }}
      maxWidth="md"
      open={true}
      tabs={[i18n.t("widget-settings.query-and-data"), i18n.t("display"), i18n.t("columns")]}
      title={i18n.t("widget-types.topic-table.settings.title")}>
      <>
        {/* Tab #1 */}
        {tabValue === 0 && (
          <>
          {props.queryInfo && (
            <>
              <TopicQuerySettings
                apiData={apiData}
                bulkExclusionDialogIsOpen={bulkExclusionDialogIsOpen}
                clusteringBias={clusteringBias}
                excludedTerms={excludedTerms}
                queryInfo={props.queryInfo}
                queryTermExclusion={queryTermExclusion}
                selectedFieldIds={selectedFieldIds}
                selectedTopicsTypes={selectedTopicsTypes}
                sortBy={sortBy}
                setBulkExclusionDialogIsOpen={handleSetBulkExclusionDialogIsOpen}
                setClusteringBias={setClusteringBias}
                setExcludedTerms={handleSetExcludedTerms}
                setQueryTermExclusion={handleSetQueryTermExclusion}
                setSelectedFieldIds={handleSetSelectedFieldIds}
                setSelectedTopicsTypes={handleSetSelectedTopicsTypes}
                setSortBy={setSortBy}
              />
            </>
          )}
          </>
        )
        }
        {tabValue === 1 &&
          <SplitSettingsWrapper>
            <SettingsOptions onChange={settingsOnChange} settingsState={settingsState}>
              {/* Widget Title */}
              <SettingsAccordion label={i18n.t("widget-settings.widget-title")} showBottomDivider>
                <SettingsWidgetTitleSelect
                  keyid="widgetTitle"
                  label={i18n.t("title")}
                  showQueryOption={true}
                  widgetTitleValue={settingsState.widgetTitle} />
                {settingsState.widgetTitle === 'custom' &&
                  <Stack direction="row" spacing={2} sx={{ marginLeft: 2 }}>
                    <Divider flexItem orientation="vertical" sx={{ width: "5px" }} />
                    <SettingsTextField
                      keyid="customWidgetTitle"
                      label={i18n.t("widget-settings.custom-text")}
                      value={settingsState.customWidgetTitle}
                    />
                  </Stack>
                }
              </SettingsAccordion>
              <SettingsAccordion label={i18n.t("graph")}>
                <SettingsToggleButton
                  keyid="density"
                  label={i18n.t("tables.row-density")}
                  optionsList={densityOptions}
                  value={settingsState.density ?? "standard"} />
                <SettingsCheckbox
                  keyid="rowStripes"
                  label={i18n.t("tables.row-stripes")}
                  checked={settingsState.rowStripes}
                />
              </SettingsAccordion>
            </SettingsOptions>
          </SplitSettingsWrapper>
        }
        {tabValue === 2 &&
          <>
            <ColumnManager
              columnModel={columnModelState}
              onColumnDialogMode={handleColumnDialogMode}
              onRemoveColumn={handleHideColumn}
              onRemoveMultipleColumns={handleRemoveMultipleColumns}
              toolbarType={ColumnToolbarType.TOPIC_TABLE}
              updateColumnModel={updateColumnModelState}
            />
            <ResponsiveDialog open={isDialogOpen} maxWidth="sm">
              <DialogTitle>{i18n.t("tables.add-columns")}</DialogTitle>
              <DialogContent>
                <StaticTableDialogContent
                  columnModelState={columnModelState}
                  onColumnsToAdd={handlePrepColumnsToAdd}
                />
              </DialogContent>
              <DialogActions>
                <Button
                  size="small"
                  variant="contained"
                  color="secondary"
                  onClick={handleCloseDialog}
                >
                  {i18n.t("cancel")}
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  onClick={() => {
                    handleAddColumns(columnsToAdd);
                    handleCloseDialog();
                  }}
                >
                  {i18n.t("add")}
                </Button>
              </DialogActions>
            </ResponsiveDialog>
          </>
        }
      </>
    </TabbedDialog>
  );
}